import React, { Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Navbar } from './components/Navbar';
import { Hero } from './components/Hero';
import { ProblemSolution } from './components/ProblemSolution';
import { TrustSection } from './components/TrustSection';
import { Process } from './components/Process';
import { Footer } from './components/Footer';
import { AuditModal } from './components/AuditModal';
import { RequestCallModal } from './components/RequestCallModal';
import { WhatsAppButton } from './components/WhatsAppButton';
import { SEO } from './components/SEO';
import { ModalProvider, useModal } from './context/ModalContext';
import { organizationSchema, websiteSchema } from './utils/structuredData';

// Lazy load route components for code splitting
const Services = React.lazy(() => import('./components/Services'));
const About = React.lazy(() => import('./components/About'));
const Contact = React.lazy(() => import('./components/Contact'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-neutral-950">
    <div className="text-white">Loading...</div>
  </div>
);

// ScrollToTop component to reset scroll on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Wrapper component to use the modal hook
const AppContent: React.FC = () => {
  const { isAuditModalOpen, closeAuditModal, isRequestCallModalOpen, closeRequestCallModal } = useModal();

  return (
    <div className="bg-neutral-950 min-h-screen text-white selection:bg-blue-500/30 selection:text-blue-200 flex flex-col">
      <ScrollToTop />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>

      <Navbar />
      <AuditModal isOpen={isAuditModalOpen} onClose={closeAuditModal} />
      <RequestCallModal isOpen={isRequestCallModalOpen} onClose={closeRequestCallModal} />
      <WhatsAppButton />
      <main className="flex-grow">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={
              <>
                <SEO />
                <Hero />
                <ProblemSolution />
                <Process />
                <TrustSection />
              </>
            } />
            <Route path="/services" element={<Services />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <HelmetProvider>
      <ModalProvider>
        <AppContent />
      </ModalProvider>
    </HelmetProvider>
  );
};

export default App;